import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import RouteSpinner from "@/components/RouteSpinner";
import ScrollToTop from "@/components/ScrollToTop";
// Eager — public-facing entry points & auth flows (most-hit, smallest cost)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import PendingVerification from "@/pages/PendingVerification";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LegalReaccept from "@/pages/LegalReaccept";
import MessagingBubble from "@/components/MessagingBubble";
import RecruiterGate from "@/components/RecruiterGate";
import LegalReacceptGate from "@/components/LegalReacceptGate";
import GlobalFooter from "@/components/GlobalFooter";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { ConfirmProvider } from "@/components/ConfirmDialog";

// Lazy — everything else (dashboards, profiles, admin, legal, etc.)
const AthleteProfile = lazy(() => import("@/pages/AthleteProfile"));
const RecruiterDashboard = lazy(() => import("@/pages/RecruiterDashboard"));
const ParentDashboard = lazy(() => import("@/pages/ParentDashboard"));
const ParentPlaybookGuide = lazy(() => import("@/pages/ParentPlaybookGuide"));
const ParentArticleDetail = lazy(() => import("@/pages/ParentArticleDetail"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAudit = lazy(() => import("@/pages/admin/AdminAudit"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminPayments = lazy(() => import("@/pages/admin/AdminPayments"));
const AdminServices = lazy(() => import("@/pages/admin/AdminServices"));
const AdminLeads = lazy(() => import("@/pages/admin/AdminLeads"));
const AdminEventSignups = lazy(() => import("@/pages/admin/AdminEventSignups"));
const AdminEventCapture = lazy(() => import("@/pages/admin/AdminEventCapture"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminScout = lazy(() => import("@/pages/admin/AdminScout"));
const AdminShare = lazy(() => import("@/pages/admin/AdminShare"));
const ScoutingReportPublic = lazy(() => import("@/pages/ScoutingReportPublic"));
const AdminTrash = lazy(() => import("@/pages/admin/AdminTrash"));
const AdminSessions = lazy(() => import("@/pages/admin/AdminSessions"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const EventsList = lazy(() => import("@/pages/EventsList"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const RegistrationSuccess = lazy(() => import("@/pages/RegistrationSuccess"));
const EvaluationReport = lazy(() => import("@/pages/EvaluationReport"));
const RecruitingProfileEditor = lazy(() => import("@/pages/RecruitingProfileEditor"));
const RecruiterBoard = lazy(() => import("@/pages/RecruiterBoard"));
const BookSession = lazy(() => import("@/pages/BookSession"));
const LegalDocPage = lazy(() => import("@/pages/LegalDocPage"));
const ParentIntake = lazy(() => import("@/pages/ParentIntake"));
const RecruiterAgreement = lazy(() => import("@/pages/RecruiterAgreement"));
const Rankings = lazy(() => import("@/pages/Rankings"));
const ForColleges = lazy(() => import("@/pages/ForColleges"));
const About = lazy(() => import("@/pages/About"));
const AthleteClaim = lazy(() => import("@/pages/AthleteClaim"));
const AthleteDashboard = lazy(() => import("@/pages/AthleteDashboard"));
const TeamManagement = lazy(() => import("@/pages/TeamManagement"));
const TeamClaim = lazy(() => import("@/pages/TeamClaim"));

const RANKINGS_OWNER_EMAIL = "brandon@omnisportsgroup.us";

function RankingsGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60">Loading…</div>;
  if (!user || (user.email || "").toLowerCase() !== RANKINGS_OWNER_EMAIL) {
    return <Navigate to="/" replace />;
  }
  return <Rankings />;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // First-login forced password change — applies to every role
  if (
    user.must_change_password &&
    typeof window !== "undefined" &&
    window.location.pathname !== "/auth/change-password"
  ) {
    return <Navigate to="/auth/change-password" replace />;
  }
  // Recruiter holding screen — bypass when already on the pending page
  if (
    user.role === "recruiter" &&
    user.verification_status &&
    user.verification_status !== "verified" &&
    typeof window !== "undefined" &&
    window.location.pathname !== "/pending-verification"
  ) {
    return <Navigate to="/pending-verification" replace />;
  }
  return children;
}

function AuthOnly({ children }) {
  // Auth-required wrapper that does NOT enforce the must_change_password gate
  // (used by the change-password page itself to avoid an infinite redirect).
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      <Navbar />
      <Suspense fallback={<RouteSpinner />}>
        <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/legal/reaccept"
          element={
            <Protected>
              <LegalReaccept />
            </Protected>
          }
        />
        <Route path="/athletes/:id" element={<AthleteProfile />} />
        <Route path="/scouting/r/:token" element={<ScoutingReportPublic />} />
        <Route path="/events" element={<EventsList />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/registrations/success" element={<RegistrationSuccess />} />
        <Route path="/evaluations/:id" element={<EvaluationReport />} />
        <Route path="/legal/:type" element={<LegalDocPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route
          path="/pending-verification"
          element={
            <Protected roles={["recruiter"]}>
              <PendingVerification />
            </Protected>
          }
        />
        <Route path="/rankings" element={<RankingsGate />} />
        <Route path="/for-colleges" element={<ForColleges />} />
        <Route path="/for-recruiters" element={<ForColleges />} />
        <Route path="/about" element={<About />} />
        <Route path="/athlete/claim" element={<AthleteClaim />} />
        <Route path="/team/claim" element={<TeamClaim />} />
        <Route
          path="/dashboard/athlete"
          element={
            <Protected roles={["athlete"]}>
              <AthleteDashboard />
            </Protected>
          }
        />
        <Route
          path="/dashboard/recruiter/team"
          element={
            <Protected roles={["recruiter"]}>
              <TeamManagement />
            </Protected>
          }
        />
        <Route
          path="/auth/change-password"
          element={
            <AuthOnly>
              <ChangePassword />
            </AuthOnly>
          }
        />
        <Route
          path="/onboarding/parent-intake"
          element={
            <Protected roles={["parent"]}>
              <ParentIntake />
            </Protected>
          }
        />
        <Route
          path="/onboarding/recruiter-agreement"
          element={
            <Protected roles={["recruiter"]}>
              <RecruiterAgreement />
            </Protected>
          }
        />
        <Route
          path="/dashboard/recruiter"
          element={
            <Protected roles={["recruiter", "super_admin", "tester_admin"]}>
              <RecruiterGate>
                <RecruiterDashboard />
              </RecruiterGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/recruiter/board"
          element={
            <Protected roles={["recruiter", "super_admin", "tester_admin"]}>
              <RecruiterGate>
                <RecruiterBoard />
              </RecruiterGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/parent"
          element={
            <Protected roles={["parent"]}>
              <LegalReacceptGate>
                <ParentDashboard />
              </LegalReacceptGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/parent/book"
          element={
            <Protected roles={["parent"]}>
              <LegalReacceptGate>
                <BookSession />
              </LegalReacceptGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/parent/profile/:athleteId"
          element={
            <Protected roles={["parent", "super_admin", "tester_admin"]}>
              <LegalReacceptGate>
                <RecruitingProfileEditor />
              </LegalReacceptGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/parent/playbook/:slug"
          element={
            <Protected roles={["parent", "super_admin", "tester_admin"]}>
              <LegalReacceptGate>
                <ParentPlaybookGuide />
              </LegalReacceptGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/parent/articles/:articleId"
          element={
            <Protected roles={["parent", "super_admin", "tester_admin"]}>
              <LegalReacceptGate>
                <ParentArticleDetail />
              </LegalReacceptGate>
            </Protected>
          }
        />
        <Route
          path="/dashboard/admin"
          element={
            <Protected roles={["super_admin", "tester_admin"]}>
              <AdminLayout />
            </Protected>
          }
        >
          <Route index element={<AdminOverview />} />
          <Route path="overview" element={<AdminOverview />} />
          <Route path="testing" element={<AdminDashboard />} />
          <Route
            path="users"
            element={
              <Protected roles={["super_admin"]}>
                <AdminUsers />
              </Protected>
            }
          />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="events" element={<AdminEvents />} />
          <Route
            path="payments"
            element={
              <Protected roles={["super_admin"]}>
                <AdminPayments />
              </Protected>
            }
          />
          <Route path="services" element={<AdminServices />} />
          <Route
            path="leads"
            element={
              <Protected roles={["super_admin"]}>
                <AdminLeads />
              </Protected>
            }
          />
          <Route path="events/:eventId/signups" element={<AdminEventSignups />} />
          <Route path="events/:eventId/capture" element={<AdminEventCapture />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="scout" element={<AdminScout />} />
          <Route path="share" element={<AdminShare />} />
          <Route
            path="sessions"
            element={
              <Protected roles={["super_admin", "tester_admin"]}>
                <AdminSessions />
              </Protected>
            }
          />
          <Route
            path="trash"
            element={
              <Protected roles={["super_admin"]}>
                <AdminTrash />
              </Protected>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <MessagingBubble />
      <GlobalFooter />
      <PwaInstallPrompt />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ConfirmProvider>
              <Shell />
              <Toaster theme="dark" position="top-right" />
            </ConfirmProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
